import { ReactComponent as ArchiveIcon } from 'assets/icons/archive.svg';
import { ReactComponent as InviteIcon } from 'assets/icons/person-plus.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { ReactComponent as LeaveIcon } from 'assets/icons/external-link.svg';
import Button from 'atoms/Button';
import Dropdown from 'atoms/Dropdown';
import TextEditable from 'atoms/TextEditable';
import { ProjectState } from 'core/model';
import ColoredDot from 'layout/atoms/ColoredDot';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { openModal } from 'store/layoutSlice';
import { leaveProject, renameProject } from 'store/projectsThunks';
import { AppDispatch } from '../../../store';
import { ChangeColor } from './ChangeColor';
import { Invite } from './Invite';
import styles from './Project.module.scss';

const Header = (props: {
    workspaceId: string;
    project: ProjectState;
    onProjectDelete?: any;
    onProjectArchive?: any;
    onProjectUnarchive?: any;
    archived?: boolean;
    modal?: boolean;
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [newProjectName, setNewProjectName] = useState<string>(
        props.project?.name || ''
    );
    const [isEditingProjectName, setIsEditingProjectName] =
        useState<boolean>(false);
    const [inviteInfoOpen, setInviteInfoOpen] = useState<boolean>(false);
    const [changeColorOpen, setChangeColorOpen] = useState<boolean>(false);

    // Guard against undefined project
    if (!props.project) {
        return null;
    }

    const startRenamingProject = () => {
        setIsEditingProjectName(true);
    };

    const handleChange = (event: any) => {
        setNewProjectName(event.target.value);
    };

    const doRenameProject = (name: string) => {
        dispatch(
            renameProject({
                projectId: props.project.id,
                command: {
                    workspaceId: props.workspaceId,
                    name: name,
                },
            })
        );
    };

    const renamingConfirmed = () => {
        if (newProjectName && newProjectName.trim()) {
            doRenameProject(newProjectName.trim());
            setNewProjectName(newProjectName.trim());
            setIsEditingProjectName(false);
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
            renamingConfirmed();
        } else if (event.key === 'Escape') {
            setIsEditingProjectName(false);
        }
    };

    const onRenameConfirmedButtonClicked = () => {
        renamingConfirmed();
    };

    const handleBlur = () => {
        setIsEditingProjectName(false);
    };

    const handleLeaveProject = () => {
        dispatch(
            leaveProject({
                projectId: props.project.id,
                command: {
                    workspaceId: props.workspaceId,
                },
            })
        );
    };

    if (isEditingProjectName) {
        return (
            <div
                onBlur={handleBlur}
                className={styles.header}
                style={{ borderColor: props.project.color || '' }}
            >
                <TextEditable
                    value={newProjectName}
                    handleChange={handleChange}
                    handleKeyDown={handleKeyDown}
                    maxLength={100}
                    placeholder="Project name..."
                    onRenameConfirmedButtonClicked={
                        onRenameConfirmedButtonClicked
                    }
                />
            </div>
        );
    }

    const onChangeColorClicked = () => {
        setChangeColorOpen(true);
    };

    const onInviteClicked = () => {
        setInviteInfoOpen(true);
    };

    const ownProject = !props.project.shared;

    if (props.archived) {
        return (
            <div
                className={styles.header}
                style={{ color: props.project.color || '' }}
            >
                <h2>{props.project.name}</h2>
                <div>
                    {!props.modal && (
                        <Button
                            size="m"
                            onClick={() =>
                                dispatch(
                                    openModal({
                                        type: 'archivedProjectDetails',
                                        id: props.project.id,
                                    })
                                )
                            }
                        >
                            Details
                        </Button>
                    )}
                    <Button size="m" onClick={props.onProjectUnarchive}>
                        Bring back
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                className={styles.header}
                style={{ color: props.project.color || '' }}
            >
                <h2 onClick={startRenamingProject}>{props.project.name}</h2>
                {ownProject && (
                    <div>
                        <Dropdown position="bottomRight" horizontal={true}>
                            <Button size="m" onClick={onChangeColorClicked}>
                                <ColoredDot color={props.project.color} />{' '}
                                <span>Change color</span>
                            </Button>
                            <Button size="m" onClick={onInviteClicked}>
                                <InviteIcon /> <span>Invite</span>
                            </Button>
                            <Button size="m" onClick={props.onProjectArchive}>
                                <ArchiveIcon /> <span>Archive</span>
                            </Button>
                            <Button size="m" onClick={props.onProjectDelete}>
                                <TrashIcon /> <span>Delete</span>
                            </Button>
                        </Dropdown>
                    </div>
                )}
                {!ownProject && (
                    <div>
                        <Dropdown position="bottomRight" horizontal={true}>
                            <Button size="m" onClick={handleLeaveProject}>
                                <LeaveIcon /> <span>Leave project</span>
                            </Button>
                        </Dropdown>
                    </div>
                )}
            </div>
            {inviteInfoOpen && (
                <Invite
                    opened={inviteInfoOpen}
                    workspaceId={props.workspaceId}
                    projectId={props.project.id}
                    handleModalClose={() => setInviteInfoOpen(false)}
                />
            )}
            {changeColorOpen && (
                <ChangeColor
                    opened={changeColorOpen}
                    workspaceId={props.workspaceId}
                    projectId={props.project.id}
                    handleModalClose={() => setChangeColorOpen(false)}
                />
            )}
        </>
    );
};

export default Header;
